import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import { useMemo } from "react"

import AssetLibraryPicker from "src/components/AssetLibraryPicker"
import { useStore } from "src/store/lib/useStore"
import { ConfigurableDynamicFormFieldsStore } from "src/components/ConfigurableDynamicFormFields/store"
import { getSectionFieldName } from "src/components/ConfigurableDynamicFormFields/helpers/fieldNameHelpers"
import { IConfigurableDynamicFormFieldsAssetLibraryPickerProps } from "src/components/ConfigurableDynamicFormFields/types/form-field"

export const ConfigurableDynamicFormFieldsAssetLibraryPicker = observer(
    ({
        activeAssetType,
        sectionName,
        field,
    }: IConfigurableDynamicFormFieldsAssetLibraryPickerProps) => {
        const store = useStore(ConfigurableDynamicFormFieldsStore)
        const formFieldKey = useMemo(
            () => getSectionFieldName(sectionName, field.name),
            [field.name, sectionName],
        )

        // Get dynamic aspect ratio from field config or default to 1/1
        const aspectRatio = useMemo(() => {
            const ratio = field.config?.image_ratio ?? "1/1"
            return ratio
        }, [field.config?.image_ratio])

        // Skip validation if no image_ratio is specified in config
        const skipAspectRatioValidation = useMemo(() => {
            return (
                field.config?.image_ratio == null ||
                field.config.image_ratio === ""
            )
        }, [field.config?.image_ratio])

        // Get tooltip text from field.tooltip_key
        const tooltipText = useMemo(() => {
            if (field.tooltip_key != null) {
                return t({ id: field.tooltip_key })
            }
            return undefined
        }, [field.tooltip_key])

        // Force reactivity by accessing form state directly in render
        const currentValue = store.form.get(formFieldKey) as string

        return (
            <AssetLibraryPicker
                key={`${formFieldKey}-${currentValue ?? "empty"}`}
                value={currentValue}
                onIconDelete={() => store.form.set(formFieldKey, "")}
                onIconChange={(images) =>
                    store.handleIconChange(images, formFieldKey)
                }
                errorMessage={store.form.error(formFieldKey) ?? ""}
                setErrorMessage={(error) =>
                    store.form.setError(formFieldKey, error)
                }
                acceptedFormats={[
                    "image/png",
                    "image/jpeg",
                    "image/jpg",
                    "image/svg+xml",
                ]}
                aspectRatio={aspectRatio}
                disabled={store.isReadOnly}
                activeAssetType={activeAssetType}
                tooltipText={tooltipText}
                skipAspectRatioValidation={skipAspectRatioValidation}
            />
        )
    },
)
